import React, { Suspense, useEffect, useState } from 'react';
import { queryCache } from 'react-query';

import { useRouter } from '../../application/Router';
import { useUser } from '../../api/hooks/useUser';
import { DeclarationActionTypes } from '../../view-models/DeclarationActionTypes';
import { MessageStatusTypes } from '../../view-models/MessageStatusTypes';
import Placeholder from '../Placeholder/Placeholder';
import { CollectionState } from '../../api/api-types';
import { userQueryKey } from '../../api/hooks/useUser';

const StatusMessageOverlay = !process.env.SERVER
  ? process.env.NODE_ENV === 'test'
    ? require('./StatusMessageOverlay').default
    : React.lazy(() => import('./StatusMessageOverlay'))
  : null;

export type StatusMessagesProps = {};

export type OverlayMessage = {
  status: MessageStatusTypes | null;
  action: DeclarationActionTypes | null;
  state?: CollectionState;
};

function shouldShowMessage(message: OverlayMessage) {
  // Always show error status, regardless of action
  if (message.status === 'error' && !message.state) return true;
  return (
    message.status &&
    message.action &&
    // If the user cancelled an action, we don't show anything.
    message.status !== MessageStatusTypes.cancelled
  );
}

function StatusMessages(props: StatusMessagesProps) {
  const { searchParams, updateSearchParams, replace } = useRouter();
  const { user } = useUser();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [message, setOverlayMessage] = useState<OverlayMessage | undefined>(
    undefined,
  );
  const isDataInitialized: boolean = user?.isDataInitialized ?? true;
  const renderStatusMessageOverlay = (
    props: React.ComponentProps<
      typeof import('./StatusMessageOverlay').default
    >,
  ) => {
    if (StatusMessageOverlay === null) return null;

    if (process.env.NODE_ENV === 'test') {
      const TestStatusMessageOverlay = StatusMessageOverlay as React.ComponentType<
        typeof props
      >;
      return <TestStatusMessageOverlay {...props} />;
    }

    return (
      <Suspense fallback={<Placeholder size="fullscreen" />}>
        <StatusMessageOverlay {...props} />
      </Suspense>
    );
  };

  useEffect(() => {
    if (isDataInitialized) setShowModal(true);
  }, [user, isDataInitialized]);

  useEffect(() => {
    if (searchParams && searchParams.has('status')) {
      // We get the state if it is passed
      const messageState = searchParams.get('state');

      let message: OverlayMessage = { status: null, action: null };

      message = {
        action: searchParams.get('action') as DeclarationActionTypes,
        status: searchParams.get('status') as MessageStatusTypes,
        state: (messageState as unknown) as CollectionState,
      };

      updateSearchParams([
        {
          key: 'action',
          value: undefined,
        },
        { key: 'status', value: undefined },
        { key: 'state', value: undefined },
      ]);

      if (
        message.hasOwnProperty('action') &&
        message.hasOwnProperty('status') &&
        shouldShowMessage(message)
      ) {
        // Signing redirects can carry a success status even when the
        // collection changed while the user was signing. Refresh the user
        // before resolving the final status message against current state.
        queryCache.invalidateQueries(userQueryKey, { throwOnError: false });
        setOverlayMessage(message);
        setShowModal(true);
      }
    }
  }, [replace, searchParams, updateSearchParams]);

  //For the cases when there was an error initializing user's data
  if (StatusMessageOverlay !== null && !isDataInitialized) {
    return renderStatusMessageOverlay({
      isOpen: showModal,
      status: 'error' as MessageStatusTypes,
      onDismiss: () => {
        setShowModal(false);
      },
    });
  }

  if (
    message &&
    message.status &&
    StatusMessageOverlay !== null &&
    isDataInitialized
  ) {
    return renderStatusMessageOverlay({
      isOpen: showModal,
      action: message.action,
      status: message.status,
      //For the cases when an error occurs when confirming or withdrawing a declaration
      state: message?.state,
      onDismiss: () => {
        setShowModal(false);
      },
    });
  }

  return null;
}

export default StatusMessages;
